import { HomeLoading } from "@/components/home-loading";
import logoColors from "@/assets/logo-colors.png";
import gorgiasLogo from "@/assets/integrations/gorgias.png";
import { useAuth } from "@babackapp/auth-client/hooks";
import useAllViewerShops from "@babackapp/private-api-client/client/use-all-viewer-shops";
import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import useFinalizeGorgiasIntegration, {
  type GorgiasApiError,
} from "@babackapp/private-api-client/client/use-finalize-gorgias-integration";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useState } from "react";
import { Navigate } from "react-router-dom";

function constructRedirectUrl(token: string, account: string) {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("token", token);
  urlSearchParams.append("account", account);
  return `/gorgias/connect?${urlSearchParams.toString()}`;
}

function LogoConnection() {
  return (
    <div className="flex items-center justify-center gap-6 mb-8 relative">
      {/* Baback Logo */}
      <div className="relative group">
        <div className="absolute inset-0 bg-purple-400/30 rounded-2xl blur-xl animate-pulse" />
        <div className="relative flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg p-3 transition-transform hover:scale-105">
          <img
            src={logoColors}
            alt="Baback"
            className="h-12 w-12 object-contain"
          />
        </div>
      </div>

      {/* Animated Connection */}
      <div className="relative w-24 h-8 flex items-center justify-center">
        {/* Background line */}
        <div className="absolute w-full h-1 bg-gradient-to-r from-purple-200 via-gray-200 to-orange-200 rounded-full" />

        {/* Animated particles */}
        <div
          className="absolute w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-purple-400 shadow-lg shadow-purple-400/50"
          style={{
            animation: "flow-right 1.5s ease-in-out infinite",
            left: 0,
          }}
        />
        <div
          className="absolute w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-400 to-orange-400 shadow-lg shadow-purple-400/30"
          style={{
            animation: "flow-right 1.5s ease-in-out infinite 0.5s",
            left: 0,
          }}
        />
        <div
          className="absolute w-2 h-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 shadow-lg shadow-orange-400/50"
          style={{
            animation: "flow-left 1.5s ease-in-out infinite 0.25s",
            right: 0,
          }}
        />

        {/* Center connection point */}
        <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white border-2 border-purple-400 flex items-center justify-center z-10">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-orange-500 animate-ping" />
        </div>
      </div>

      {/* Gorgias Logo */}
      <div className="relative group">
        <div className="absolute inset-0 bg-orange-400/30 rounded-2xl blur-xl animate-pulse" />
        <div className="relative flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg p-3 transition-transform hover:scale-105">
          <img
            src={gorgiasLogo}
            alt="Gorgias"
            className="h-12 w-12 object-contain"
          />
        </div>
      </div>

      {/* Keyframes styles */}
      <style>
        {`
          @keyframes flow-right {
            0% {
              left: 0;
              opacity: 0;
              transform: scale(0.5);
            }
            10% {
              opacity: 1;
              transform: scale(1);
            }
            90% {
              opacity: 1;
              transform: scale(1);
            }
            100% {
              left: calc(100% - 12px);
              opacity: 0;
              transform: scale(0.5);
            }
          }
          @keyframes flow-left {
            0% {
              right: 0;
              opacity: 0;
              transform: scale(0.5);
            }
            10% {
              opacity: 1;
              transform: scale(1);
            }
            90% {
              opacity: 1;
              transform: scale(1);
            }
            100% {
              right: calc(100% - 8px);
              opacity: 0;
              transform: scale(0.5);
            }
          }
        `}
      </style>
    </div>
  );
}

export default function GorgiasConnectPage() {
  const t = useTranslation("gorgiasConnect");
  const auth = useAuth();
  const { data: ownShops, isPending: isOwnShopsLoading } = useAllViewerShops();
  // A super admin sets Gorgias up for a merchant without being a member of the
  // shop (BAB-3035): every shop is offered, searchable.
  const isSuperAdmin = useViewerIsSuperAdmin();
  const { data: allShops, isPending: isAllShopsLoading } =
    useSuperAdminAllShops(isSuperAdmin);
  const shops = isSuperAdmin ? allShops : ownShops;
  const isShopsLoading = isSuperAdmin ? isAllShopsLoading : isOwnShopsLoading;
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const token = new URLSearchParams(window.location.search).get("token");
  const account = new URLSearchParams(window.location.search).get("account");

  const finalizeMutation = useFinalizeGorgiasIntegration({
    onSuccess: () => {
      setIsSuccess(true);
      // Redirect to Gorgias after a short delay
      setTimeout(() => {
        window.location.href = `https://${account}.gorgias.com/app/home`;
      }, 2000);
    },
    onError: (err: GorgiasApiError) => {
      setErrorCode(err.code || "UNKNOWN_ERROR");
    },
  });

  // Missing required params
  if (!token || !account) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invalidLink.title")}</CardTitle>
            <CardDescription>{t("invalidLink.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Loading auth state
  if (auth.isAuthenticating) return <HomeLoading />;

  // Not authenticated - redirect to sign in
  if (!auth.isAuthenticated) {
    const redirectUrl = constructRedirectUrl(token, account);
    return (
      <Navigate
        to={`/auth/sign-in?redirectTo=${encodeURIComponent(redirectUrl)}`}
        replace
      />
    );
  }

  // Loading shops
  if (isShopsLoading) return <HomeLoading />;

  // No shops found
  if (!shops || shops.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("noShops.title")}</CardTitle>
            <CardDescription>{t("noShops.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600">
              {t("success.title")}
            </CardTitle>
            <CardDescription>{t("success.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleConnect = () => {
    if (!selectedShopId || !token) return;
    finalizeMutation.mutate({ shopId: selectedShopId, token });
  };

  const selectedShop = shops.find((shop) => shop.id === selectedShopId);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
      <LogoConnection />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>{t("title")}</CardTitle>
          <CardDescription>
            {t("subtitle")} <strong>{account}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {errorCode && (
            <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
              <p className="font-medium">{t(`errors.${errorCode}`)}</p>
              {errorCode === "GORGIAS_EXTENSION_NOT_ENABLED" && (
                <a
                  href="mailto:support@baback.co"
                  className="mt-2 inline-block text-red-600 underline hover:text-red-800"
                >
                  support@baback.co
                </a>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t("selectShop")}
              </label>
              {isSuperAdmin ? (
                <SuperAdminShopPicker
                  shops={shops}
                  selectedShopId={selectedShopId}
                  onSelect={setSelectedShopId}
                />
              ) : (
                <Select
                  value={selectedShopId ?? undefined}
                  onValueChange={setSelectedShopId}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("selectShopPlaceholder")}>
                      {selectedShop && (
                        <div className="flex flex-col items-start">
                          <span>{selectedShop.name}</span>
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(ownShops ?? []).map((shop) => (
                      <SelectItem key={shop.id} value={shop.id}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{shop.name}</span>
                          {shop.shopifyUrl && (
                            <span className="text-xs text-gray-500">
                              {shop.shopifyUrl}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <Button
              onClick={handleConnect}
              disabled={!selectedShopId || finalizeMutation.isPending}
              className="w-full"
            >
              {finalizeMutation.isPending ? t("connecting") : t("connect")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SuperAdminShopPicker({
  shops,
  selectedShopId,
  onSelect,
}: {
  shops: { id: string; name: string; shopifyUrl?: string | null }[];
  selectedShopId: string | null;
  onSelect: (shopId: string) => void;
}) {
  return (
    <Command className="rounded-md border">
      <CommandInput placeholder="Nom, domaine Shopify ou id…" />
      <CommandList className="max-h-56">
        <CommandEmpty>Aucune boutique.</CommandEmpty>
        <CommandGroup>
          {shops.map((shop) => (
            <CommandItem
              key={shop.id}
              value={`${shop.name}|${shop.shopifyUrl ?? ""}|${shop.id}`}
              onSelect={() => onSelect(shop.id)}
              className={
                shop.id === selectedShopId
                  ? "bg-accent font-semibold"
                  : undefined
              }
            >
              <span className="flex-1 truncate">{shop.name}</span>
              <span className="text-xs text-gray-500">
                {shop.shopifyUrl ?? shop.id}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
